import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostBinding,
  ViewChild,
  ElementRef,
  Renderer2,
  TemplateRef,
} from "@angular/core";
import { MatMenu } from "@angular/material/menu";
import { tr } from "@util/tr";
import * as moment from "moment";

@Component({
  selector: "ws-selector",
  templateUrl: "./ws-selector.component.html",
  styleUrls: ["./ws-selector.component.scss"],
})
export class WsSelectorComponent implements OnChanges {
  @Input() options: Array<any> | null = [];
  @Input() selected: any = null;
  @Input() selectedId: string | undefined;
  @Output() selectedChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() fixedText: string | undefined;
  @Input() placeholder: string = "Select";
  @Input() formatter: (item: any) => string = (item) =>
    typeof item === "string" ? item : tr(item?.name) || tr("None");
  @Input() tooltip: string = tr("Select an option");
  @Input() dropdownWidth?: string;
  @Input() align: "center" | "justify" = "center";
  @Input() multiSelect: boolean = false;
  @Input() disabled: boolean = false;
  @Input() optionTemplate: TemplateRef<{ $implicit: any }> | null = null;
  @Input() triggerTemplate: TemplateRef<{ $implicit: any; placeholder: string }> | null =
    null;
  @Input() panelClass: string | string[] = "";
  @Input() optionItemClass: string | string[] = "";
  @Input() emitOnClickMultiple: boolean = false;
  @Output() selectedItemsChange: EventEmitter<any[]> = new EventEmitter<
    any[]
  >();

  // Calendar functionality
  @Input() useCalendar: boolean = false;
  @Input() selectedDate: moment.Moment | null = null;
  @Output() dateSelected: EventEmitter<moment.Moment> = new EventEmitter<moment.Moment>();

  @ViewChild('selectorMenu', { static: true }) selectorMenu!: MatMenu;

  _selectedItems: any[] = [];

  @Input() set selectedItems(value: any[]) {
    this._selectedItems = value || [];
  }

  menuOpen: boolean = false;
  highlightedIndex: number = 0; // For keyboard navigation

  constructor(private renderer: Renderer2, private elementRef: ElementRef) {}

  private setMenuWidth(): void {
    if (this.dropdownWidth) {
      // Use a timeout to ensure the menu panel is rendered
      setTimeout(() => {
        const menuPanel = document.querySelector('.mat-menu-panel') as HTMLElement;
        if (menuPanel) {
          this.renderer.setStyle(menuPanel, 'width', this.dropdownWidth);
          this.renderer.setStyle(menuPanel, 'max-width', this.dropdownWidth);
        }
      }, 0);
    }
  }

  @HostBinding("style.--dropdown-width")
  get dynamicdropdownWidth(): string | undefined {
    return this.dropdownWidth;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes["options"] || changes["selectedId"]) && this.options) {
      this.preselectOption();
    }
    
    if (changes["selected"]) {
      const selectedValue = changes["selected"].currentValue;
      if (selectedValue && this.options) {
        // Find the matching option in the options array
        const matchingOption = this.options.find(option => 
          this.compareItems(option, selectedValue)
        );
        if (matchingOption) {
          this.selected = matchingOption;
          this._selectedItems = [matchingOption];
        } else {
          this.selected = selectedValue;
          this._selectedItems = [selectedValue];
        }
      } else if (!selectedValue) {
        this.selected = null;
        this._selectedItems = [];
      }
    }

    // Handle selectedItems changes
    if (changes["selectedItems"]) {
      this._selectedItems = changes["selectedItems"].currentValue || [];
      if (this.multiSelect) {
        this.syncMultiSelectFromSelectedItems();
      } else {
        this.syncSingleSelectFromSelectedItems();
      }
    }

    if (changes["options"] && this._selectedItems?.length) {
      if (this.multiSelect) {
        this.syncMultiSelectFromSelectedItems();
      } else {
        this.syncSingleSelectFromSelectedItems();
      }
    }
  }

  selectOption(option: any, event?: Event): void {
    if (this.multiSelect) {
      if (event) {
        event.stopPropagation(); // Prevent menu from closing in multi-select mode
      }

      const index = this._selectedItems.findIndex(
        (item) => this.compareItems(item, option)
      );

      if (index > -1) {
        this._selectedItems = this._selectedItems.filter(
          (item, i) => i !== index
        );
      } else {
        this._selectedItems = [...this._selectedItems, option];
      }

      this._selectedItems = this.getOptionsWithSelection().filter(
        (item) => item.selected
      );

      this.selectedItemsChange.emit(this.getOptionsWithSelection());
    } else {
      this.selected = option;
      this._selectedItems = [option];
      this.selectedChange.emit(option);
      this.selectedItemsChange.emit([option]);
    }
  }

  getOptionsWithSelection(): any[] {
    if (!this.options) return [];
    return this.options.map((opt) => ({
      ...opt,
      selected: this._selectedItems.some(
        (selected) => this.compareItems(selected, opt)
      ),
    }));
  }

  private compareItems(item1: any, item2: any): boolean {
    if (!item1 || !item2) return false;

    if (item1.id != null && item2.id != null) {
      return item1.id === item2.id;
    }

    if (item1.attributeId != null && item2.attributeId != null) {
      return String(item1.attributeId) === String(item2.attributeId);
    }

    if (item1.valueId != null && item2.valueId != null) {
      return String(item1.valueId) === String(item2.valueId);
    }

    if (item1.iso && item2.iso) {
      return item1.iso === item2.iso;
    }

    return item1 === item2;
  }

  /** Resolves `id`, `attributeId`, or `valueId` for option lists that do not use `id`. */
  private optionPrimaryId(option: any): string {
    if (option == null) return "";
    return String(option.id ?? option.attributeId ?? option.valueId ?? "");
  }

  /** When parent drives selection via [selectedItems] without [selectedId], keep the trigger label in sync with options. */
  private syncSingleSelectFromSelectedItems(): void {
    if (this.multiSelect) return;
    if (!this._selectedItems?.length) {
      this.selected = null;
      return;
    }
    const cur = this._selectedItems[0];
    if (this.options?.length) {
      const match = this.options.find((o) => this.compareItems(o, cur));
      if (match) {
        this.selected = match;
        this._selectedItems = [match];
        return;
      }
    }
    this.selected = cur;
  }

  /** Align parent-driven selection with current `options` (same references for labels/checkboxes). */
  private syncMultiSelectFromSelectedItems(): void {
    if (!this.multiSelect || !this._selectedItems?.length || !this.options?.length) {
      return;
    }
    this._selectedItems = this._selectedItems
      .map((cur) => this.options!.find((o) => this.compareItems(o, cur)) ?? cur)
      .filter((cur) => this.options!.some((o) => this.compareItems(o, cur)));
  }

  private preselectOption(): void {
    if (this.multiSelect) {
      this._selectedItems =
        this.options?.filter((option) =>
          this.selectedId?.includes(option?.id)
        ) || [];
      //this.selectedItemsChange.emit(this.selectedItems);
    } else {
      const wantId =
        this.selectedId === undefined || this.selectedId === null
          ? ""
          : String(this.selectedId);
      if (!wantId) {
        this.syncSingleSelectFromSelectedItems();
        return;
      }
      const match = this.options?.find(
        (option) => this.optionPrimaryId(option) === wantId
      );
      if (match) {
        this.selected = match;
        this._selectedItems = [match];
        // Do not emit selectedChange here: parent already drives [selectedId].
        // Re-emitting overwrites a just-chosen option (N-7b rate name presets).
      } else {
        this.selected = null;
        this._selectedItems = [];
      }
    }
  }

  isSelected(option: any): boolean {
    return this.multiSelect
      ? this._selectedItems.some((item) => this.compareItems(item, option))
      : this.compareItems(this.selected, option);
  }

  setMenuOpen(state: boolean): void {
    this.menuOpen = state;
  }

  onMenuOpened(): void {
    this.menuOpen = true;
    this.setMenuWidth();
  }

  onMenuClosed(): void {
    this.menuOpen = false;
    if (this.multiSelect) {
      this.syncMultiSelectFromSelectedItems();
      this.selectedItemsChange.emit(
        this.getOptionsWithSelection()
      );
    } else {
      this.syncSingleSelectFromSelectedItems();
      this.selectedItemsChange.emit(
        this.selected ? [this.selected] : []
      );
    }
  }

  /** Keyboard navigation **/
  onKeydown(event: KeyboardEvent): void {
    if (!this.options?.length) return;

    if (event.key === "ArrowDown") {
      this.highlightedIndex = (this.highlightedIndex + 1) % this.options.length;
      event.preventDefault();
    } else if (event.key === "ArrowUp") {
      this.highlightedIndex =
        (this.highlightedIndex - 1 + this.options.length) % this.options.length;
      event.preventDefault();
    } else if (event.key === "Enter") {
      this.selectOption(this.options[this.highlightedIndex]);
      event.preventDefault();
    }
  }

  onDateChange(selectedDate: moment.Moment): void {
    this.selectedDate = selectedDate;
    this.dateSelected.emit(selectedDate);
    this.setMenuOpen(false);
    this.selectorMenu.closed.emit();
  }
}
